const EntryService = require('../services/EntryService').default;
const entryCreate = require('./contentful/entry-create');
const entryUpdate = require('./contentful/entry-update');
const getSpace = require('./contentful/get-space');
const transformer = require('./transformers/generation');
const linkResolve = require('./contentful/link-resolve');
const processError = require('./contentful/error-process');
const modelModel = require('./model');

const gettingEntryService = getSpace().then((space) => {
  return new EntryService(space, transformer);
});

const createGeneration = (space, generation, depth) => {
  const data = transformer.internalToContentful(generation);
  return entryCreate(space, 'generation', data).then((entry) => {
    const data = entry.toPlainObject();
    return linkResolve(space, data, depth);
  }).then((data) => {
    return transformer.contentfulToInternal(data);
  });
};

const updateGeneration = (space, generation, depth) => {
  return space.getEntry(generation.id).catch(
    processError
  ).then((entry) => {
    const data = transformer.internalToContentful(generation);
    return entryUpdate(entry, data).then((entry) => {
      const data = entry.toPlainObject();
      return linkResolve(space, data, depth);
    }).then((data) => {
      return transformer.contentfulToInternal(data);
    });
  });
};

module.exports = {
  getById(...parameters) {
    return gettingEntryService.then((service) => service.getById(...parameters));
  },

  publish(...parameters) {
    return gettingEntryService.then((service) => service.publish(...parameters));
  },

  unpublish(...parameters) {
    return gettingEntryService.then((service) => service.unpublish(...parameters));
  },

  archive(...parameters) {
    return gettingEntryService.then((service) => service.archive(...parameters));
  },

  unarchive(...parameters) {
    return gettingEntryService.then((service) => service.unarchive(...parameters));
  },

  save(generation, depth = 0) {
    return getSpace().then((space) => {
      if (generation.id === 'temp') {
        return createGeneration(space, generation, depth);
      }
      return updateGeneration(space, generation, depth);
    });
  },

  createEmpty(modelId) {
    return modelModel.getById(modelId, 2).then((model) => {
      return {
        published: null,
        updated: null,
        draft: null,
        archived: null,
        contentType: null,
        name: model.name,
        make: model.make,
        model: model,
        id: 'temp',
        startYear: null,
        endYear: null,
        listImage: null,
        files: [],
        articles: []
      };
    });
  },

  generationByModel(modelId, depth = 0) {
    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'generation', // eslint-disable-line
        'fields.model.sys.id': modelId,
        'order': '-sys.updatedAt',
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((response) => {
        const data = response.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  generationByMake(makeId, depth = 0) {
    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'generation', // eslint-disable-line
        'fields.make.sys.id': makeId,
        'order': '-sys.updatedAt',
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((response) => {
        const data = response.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  generationsByArticleLink(articleId, depth = 0) {
    return getSpace().then((space) => {
      return space.getEntries({
        content_type: 'generation',
        links_to_entry: articleId,
        order: 'fields.name,fields.startYear'
      }).catch(
        processError
      ).then((response) => {
        const data = response.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  all(depth = 0) {
    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'generation', // eslint-disable-line
        'order': '-sys.updatedAt',
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((response) => {
        const data = response.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  select(fieldString) {
    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'generation', // eslint-disable-line
        'order': '-sys.updatedAt',
        'select': fieldString,
        'limit': 1000,
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      );
    });
  },

  // For use in selectors
  search({query, limit, offset, depth = 0}) {
    return getSpace().then((space) => {
      return space.getEntries({
        content_type: 'generation',
        order: 'fields.name,fields.startYear',
        query: query,
        skip: offset,
        limit: limit
      }).catch(
        processError
      ).then((response) => {
        const data = response.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return {
          items: data.items.map(transformer.contentfulToInternal),
          limit: data.limit,
          offset: data.skip,
          total: data.total
        };
      });
    });
  }
};
